import React, { useState, useEffect } from "react";
import {
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  Chip,
  Box,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { GET, BASE_URL } from "APIclient";
import properties from "constants/ApplicationConstants";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";

const ExitInterviewList = () => {
  const navigate = useNavigate();

  const [exitInterviews, setExitInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState("success");
  const [snackMessage, setSnackMessage] = useState("");

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search parameters - UPDATED to include employeeName
  const [searchParams, setSearchParams] = useState({
    employeeId: "",
    employeeName: ""
  });

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  // Transform data to add sequential IDs
  const transformPaginatedData = (paginatedData, currentPage, pageSize) => {
    return paginatedData.map((item, index) => ({
      ...item,
      id: currentPage * pageSize + index + 1
    }));
  };

  // Fetch all exit interviews with pagination - UPDATED to include employeeName
  const fetchAllExitInterviews = async (
    page = 0,
    pageSize = 10,
    employeeId = "",
    employeeName = ""
  ) => {
    setLoading(true);
    try {
      // Try multiple token locations
      const jwtToken =
        window.sessionStorage.getItem("authToken") ||
        window.sessionStorage.getItem("token") ||
        window.localStorage.getItem("jwtToken") ||
        window.localStorage.getItem("authToken");

      if (!jwtToken) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("size", pageSize);

      // Send both employeeId and employeeName parameters
      params.append("employeeId", employeeId?.trim() || "");
      params.append("employeeName", employeeName?.trim() || "");

      const url = `api/exit/interview?${params.toString()}`;

      const response = await GET(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`
        }
      });

      if (response.status === 200) {
        const paginatedData = response.data?.data?.paginatedData ?? response.data?.content ?? [];
        const total = response.data?.data?.totalElements ?? response.data?.totalElements ?? 0;
        const pages = response.data?.data?.totalPages ?? response.data?.totalPages ?? 0;

        const filteredData = paginatedData.filter(
          item => item.status?.toUpperCase() !== "DRAFT"
        );

        const transformed = transformPaginatedData(filteredData, page, pageSize);
        setExitInterviews(transformed);


        setTotalItems(filteredData.length);
        setTotalPages(Math.ceil(filteredData.length / pageSize));
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Failed to load exit interviews");
      setSnackColor("error");
      setSnackOpen(true);
      setExitInterviews([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Search handler - UPDATED to include employeeName
  const handleSearch = async () => {
    setSearchLoading(true);
    setPaginationModel({ ...paginationModel, page: 0 });

    await fetchAllExitInterviews(
      0,
      paginationModel.pageSize,
      searchParams.employeeId,
      searchParams.employeeName
    );

    setSearchLoading(false);
    setSnackMessage("Search completed successfully");
    setSnackColor("success");
    setSnackOpen(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Reset handler - UPDATED to reset employeeName
  const handleReset = () => {
    setSearchParams({
      employeeId: "",
      employeeName: ""
    });
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchAllExitInterviews(0, 10, "", "");
  };

  // Pagination change handler - UPDATED to include employeeName
  const handlePaginationChange = newModel => {
    setPaginationModel(newModel);
    fetchAllExitInterviews(
      newModel.page,
      newModel.pageSize,
      searchParams.employeeId,
      searchParams.employeeName
    );
  };

  // View handler - now opens PDF preview
  const handleView = row => {
    handlePreview(row);
  };

  // Edit/Fill handler
  const handleEdit = row => {
    localStorage.setItem("resignationEndDate", row.dateOfLeaving);
    navigate("/exitInterviewForm", {
      state: {
        mode: "edit",
        employeeId: row.employeeId,
        employeeName: row.employeeName,
        resignationId: row.resignationRequestId
      }
    });
  };

  // Download handler
  const handleDownload = async row => {
    try {
      const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const url = `${BASE_URL}/api/exit/interview/download/${row.employeeId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf"
        },
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error(`Exit interview not yet submitted`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error("Received empty PDF file");
      }

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      const employeeName = row.employeeName?.trim().replace(/\s+/g, "_") || "Employee";
      link.download = `${employeeName}-Exit_Interview.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(link.href), 100);

      setSnackMessage("Exit interview downloaded successfully!");
      setSnackColor("success");
      setSnackOpen(true);
    } catch (error) {
      console.error("Download error:", error);
      setSnackMessage(error.message || "Failed to download exit interview.");
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  // Preview handler (updated)
  const handlePreview = async row => {
    try {
      const token =
        localStorage.getItem("jwtToken") ||
        sessionStorage.getItem("jwtToken");

      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const url = `${BASE_URL}/api/exit/interview/preview/${row.employeeId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf"
        },
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error("Exit interview not yet submitted");
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("Received empty PDF file");
      }

      // -------- NEW PART (same behavior as second code) --------
      const employeeName =
        row.employeeName?.trim().replace(/\s+/g, "_") || "Employee";
      const filename = `${employeeName}-Exit_Interview.pdf`;

      const fileURL = window.URL.createObjectURL(blob);

      // Create hidden download link (enables Chrome download)
      const tempLink = document.createElement("a");
      tempLink.href = fileURL;
      tempLink.download = filename;
      tempLink.style.display = "none";
      document.body.appendChild(tempLink);
      document.body.removeChild(tempLink);

      // Open PDF in new tab using iframe
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${filename}</title>
            <style>
              html, body {
                margin: 0;
                padding: 0;
                height: 100%;
                overflow: hidden;
              }
              iframe {
                width: 100%;
                height: 100%;
                border: none;
              }
            </style>
          </head>
          <body>
            <iframe src="${fileURL}" type="application/pdf"></iframe>
          </body>
        </html>
      `);
        newWindow.document.close();
      }

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 60000);
      // --------------------------------------------------------

    } catch (error) {
      console.error("Preview error:", error);
      setSnackMessage(error.message || "Failed to preview exit interview.");
      setSnackColor("error");
      setSnackOpen(true);
    }
  };


  useEffect(() => {
    fetchAllExitInterviews(paginationModel.page, paginationModel.pageSize, "", "");
  }, []);

  // Status chip helper
  const getStatusChip = status => {
    if (!status) return <Chip label="N/A" size="small" />;

    const statusUpper = status.toUpperCase();
    let color = "default";

    if (statusUpper === "COMPLETED" || statusUpper === "SUBMITTED") {
      color = "success";
    } else if (statusUpper === "DRAFT" || statusUpper === "PENDING") {
      color = "warning";
    } else if (statusUpper === "NOT STARTED") {
      color = "error";
    }

    return <Chip label={status} color={color} size="small" />;
  };

  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 70,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "employeeId",
      headerName: "Employee ID",
      width: 130,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 200
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 170
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      headerAlign: "center",
      align: "center",
      type: "actions",
      getActions: params => {
        const status = params.row.status?.toLowerCase() || "";
        const actions = [];

        // View action - always available
        actions.push(
          <Tooltip title="View Interview" arrow key="view">
            <GridActionsCellItem
              icon={<VisibilityIcon />}
              label="View"
              color="primary"
              onClick={() => handleView(params.row)}
            />
          </Tooltip>
        );

        // Edit action - always available (can fill new or edit existing)
        if (status !== "submitted_by_admin") {
          actions.push(
            <Tooltip title="Edit Interview" arrow key="edit">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                color="info"
                onClick={() => handleEdit(params.row)}
              />
            </Tooltip>
          );
        }
        actions.push(
          <Tooltip title="Download PDF" arrow key="download">
            <GridActionsCellItem
              icon={<DownloadIcon />}
              label="Download"
              color="success"
              onClick={() => handleDownload(params.row)}
            />
          </Tooltip>
        );

        return actions;
      }
    }
  ];

  if (loading && paginationModel.page === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Card>
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={2}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              Exit Interview Management
            </MDTypography>
            <MDTypography variant="body2" color="white" opacity={0.8}>
              View and manage exit interview records
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            {/* Search Section - UPDATED with Employee Name field */}
            <Card sx={{ p: 2, mb: 3, backgroundColor: "#f8f9fa" }}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Search Exit Interview
              </MDTypography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={4}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Employee ID"
                    variant="standard"
                    value={searchParams.employeeId}
                    onChange={e => setSearchParams({ ...searchParams, employeeId: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Employee ID"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Employee Name"
                    variant="standard"
                    value={searchParams.employeeName}
                    onChange={e => setSearchParams({ ...searchParams, employeeName: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Employee Name"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={searchLoading}
                      sx={{ background: properties.colors.bgcolorbluethem }}
                    >
                      {searchLoading ? "Searching..." : "Search"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                    >
                      Reset
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>

            {/* Data Grid */}
            <div style={{ height: 500, width: "100%" }}>
              <DataGrid
                rows={exitInterviews}
                columns={columns}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                rowCount={totalItems}
                pageSizeOptions={[10, 25, 50]}
                loading={loading}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none"
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f5f5f5"
                  }
                }}
              />
            </div>

            <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="caption" color="text">
                Showing {exitInterviews.length} of {totalItems} records
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Page {paginationModel.page + 1} of {totalPages || 1}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        {/* View Details Modal */}
        <Dialog
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2
            }
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
              color: "white",
              py: 2.5
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  Exit Interview Details
                </MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                  Complete interview information
                </MDTypography>
              </MDBox>
              <IconButton
                onClick={() => setOpenViewModal(false)}
                size="small"
                sx={{
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }
                }}
              >
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            {selectedInterview && (
              <MDBox>
                {/* Employee Information */}
                <Card sx={{ m: 3, mb: 2, backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0" }}>
                  <MDBox p={3}>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 4,
                          height: 24,
                          backgroundColor: "#1A73E8",
                          borderRadius: 1,
                          mr: 1.5
                        }}
                      />
                      <MDTypography variant="h6" fontWeight="bold" color="info">
                        Employee Information
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Employee ID
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.employeeId || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Employee Name
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.employeeName || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Designation
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.designation || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Project
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.project || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Interview Status
                          </MDTypography>
                          <MDBox mt={0.5}>{getStatusChip(selectedInterview.status)}</MDBox>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Interview Date
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.interviewDate
                              ? new Date(selectedInterview.interviewDate).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric"
                                }
                              )
                              : "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>

                {/* Exit Details */}
                <Card sx={{ m: 3, mb: 2, backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0" }}>
                  <MDBox p={3}>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <Box
                        sx={{
                          width: 4,
                          height: 24,
                          backgroundColor: "#1A73E8",
                          borderRadius: 1,
                          mr: 1.5
                        }}
                      />
                      <MDTypography variant="h6" fontWeight="bold" color="info">
                        Exit Information
                      </MDTypography>
                    </MDBox>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={4}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Date of Joining
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.dateOfJoining
                              ? new Date(selectedInterview.dateOfJoining).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "2-digit"
                                }
                              )
                              : "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Date of Leaving
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.dateOfLeaving
                              ? new Date(selectedInterview.dateOfLeaving).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "2-digit"
                                }
                              )
                              : "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            sx={{ opacity: 0.7 }}
                          >
                            Method of Leaving
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                            {selectedInterview.methodOfLeaving || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>

                {/* Interview Responses - Only show if interview is completed */}
                {selectedInterview.reasonsForLeaving && (
                  <Card sx={{ m: 3, mb: 2, backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0" }}>
                    <MDBox p={3}>
                      <MDBox display="flex" alignItems="center" mb={2}>
                        <Box
                          sx={{
                            width: 4,
                            height: 24,
                            backgroundColor: "#1A73E8",
                            borderRadius: 1,
                            mr: 1.5
                          }}
                        />
                        <MDTypography variant="h6" fontWeight="bold" color="info">
                          Interview Responses
                        </MDTypography>
                      </MDBox>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                            Primary Reasons for Leaving:
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {selectedInterview.reasonsForLeaving || "Not provided"}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                            Most Satisfying Aspects:
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {selectedInterview.mostSatisfying || "Not provided"}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography variant="body2" fontWeight="medium" mb={0.5}>
                            Suggestions for Improvement:
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {selectedInterview.suggestionsForImprovement || "Not provided"}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                )}
              </MDBox>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setOpenViewModal(false)}
              sx={{ background: properties.colors.bgcolorbluethem }}
            >
              Close
            </MDButton>
            {selectedInterview &&
              (selectedInterview.status?.toLowerCase() === "completed" ||
                selectedInterview.status?.toLowerCase() === "submitted") && (
                <>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => {
                      setOpenViewModal(false);
                      handlePreview(selectedInterview);
                    }}
                  >
                    Preview PDF
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      setOpenViewModal(false);
                      handleDownload(selectedInterview);
                    }}
                  >
                    Download PDF
                  </MDButton>
                </>
              )}
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackOpen}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={3000}
          onClose={handleSnackClose}
        >
          <Alert
            onClose={handleSnackClose}
            severity={snackColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{ width: "100%", color: "#fff" }}
          >
            {snackMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default ExitInterviewList;
